/** Per-strategy alert sounds. Stored in localStorage because browser notifications are inherently
 * client-side (per-browser), so this needs no backend persistence. A strategy has a sound for each
 * event (entry, exit, target, alert); an event with none saved plays the strategy's old single choice,
 * then the default. */

import { DEFAULT_CHIME } from "./notify";

export const SOUND_EVENTS = [
  { id: "entry", label: "Entry" },
  { id: "exit", label: "Exit" },
  { id: "target", label: "Target" },
  { id: "alert", label: "Alert" },
] as const;
export type SoundEvent = (typeof SOUND_EVENTS)[number]["id"];

function storageKey(strategyId: string): string {
  return `nss.notify.sound.${strategyId}`;
}

function eventKey(strategyId: string, event: SoundEvent): string {
  return `${storageKey(strategyId)}.${event}`;
}

function read(key: string): string | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** The one sound this strategy had before there was one for each event. */
export function getStrategyChime(strategyId: string): string {
  return read(storageKey(strategyId)) ?? DEFAULT_CHIME;
}

export function setStrategyChime(strategyId: string, presetId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(storageKey(strategyId), presetId);
}

export type SoundMode = "LIVE" | "PAPER";

/** What each event plays for a strategy that has no choice of its own (operator, 2026-11-04). A LIVE
 * strategy: an entry says "Attention", an exit "Hey, take a look", a target is a banjo, an alert
 * trumpets. A PAPER strategy has other sounds, so a paper trade is never mistaken for a live one by
 * ear (the operator will say what they should be). */
export const DEFAULT_SOUNDS: Record<SoundMode, Record<SoundEvent, string>> = {
  LIVE: { entry: "attention", exit: "hey-take-a-look", target: "banjo", alert: "trumpets" },
  PAPER: { entry: "droplet", exit: "knock-knock", target: "promise", alert: "hand-bell" },
};

/** The sound for one event of a strategy: its own choice, else the single sound it had before there was
 * one for each event, else the default of the event for a live or a paper strategy. */
export function getStrategySound(
  strategyId: string,
  event: SoundEvent,
  mode: SoundMode = "LIVE",
): string {
  return (
    read(eventKey(strategyId, event)) ?? read(storageKey(strategyId)) ?? DEFAULT_SOUNDS[mode][event]
  );
}

export function setStrategySound(strategyId: string, event: SoundEvent, soundId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(eventKey(strategyId, event), soundId);
}
